import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatSliderModule } from '@angular/material/slider';

import { ChartPreviewKind, WidgetConfig } from './self-service-types';

/**
 * Renders an interactive widget chart type (input-box, slider, button, notes,
 * configurable form, editable table, upload file, …) from a {@link WidgetConfig}.
 *
 * Self-contained on purpose: it uses only Angular Material + plain HTML so the
 * SAME component can preview the widget in the chart-builder and render it in
 * the dashboard-builder cards without dragging in the global filter / config /
 * HTTP services that the production `app-*` widget components depend on. The
 * fully-wired published view still routes to those production components via
 * ChartRendererService — this component is the builder/dashboard preview.
 *
 * Every config value has a sensible default so a freshly-picked widget shows a
 * working control immediately, before the user has filled in any settings.
 */
@Component({
  selector: 'app-self-service-widget',
  standalone: true,
  imports: [CommonModule, FormsModule, MatButtonModule, MatSliderModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './self-service-widget.component.html',
  styleUrl: './self-service-widget.component.scss',
})
export class SelfServiceWidgetComponent {
  /** Which widget to render. */
  readonly kind = input.required<ChartPreviewKind>();
  /** The widget settings (already merged with caller-side defaults is fine —
   *  this component also applies its own defaults defensively). */
  readonly config = input<WidgetConfig | null | undefined>(null);
  /** Fallback display title (the chart name) when no explicit label is set. */
  readonly title = input<string>('');
  /** When true the notes/upload widgets become editable and emit configChange.
   *  Used by the dashboard-builder arrange step; the published view leaves it
   *  off so the production components handle interaction instead. */
  readonly editable = input<boolean>(false);

  /** Emits the next WidgetConfig whenever the user edits an editable widget. */
  readonly configChange = output<WidgetConfig>();
  /** Emits live widget values so dashboard computations can react immediately. */
  readonly valueChange = output<Record<string, unknown>>();

  // ── Resolved, defaulted views of the config ──────────────────────────────
  readonly cfg = computed<WidgetConfig>(() => this.config() ?? {});

  readonly label = computed(() => this.cfg().label || this.title() || '');

  readonly min = computed(() => this.numberOr(this.cfg().min, 0));
  readonly max = computed(() => this.numberOr(this.cfg().max, 100));
  readonly step = computed(() => {
    const s = this.numberOr(this.cfg().step, 1);
    return s > 0 ? s : 1;
  });
  readonly sliderValue = computed(() => this.clamp(this.numberOr(this.cfg().defaultValue, this.min())));
  readonly rangeStart = computed(() => this.clamp(this.numberOr(this.cfg().rangeStart, this.min())));
  readonly rangeEnd = computed(() => this.clamp(this.numberOr(this.cfg().rangeEnd, this.max())));

  readonly buttonLabel = computed(() => this.cfg().buttonLabel || this.label() || 'Button');
  readonly buttonStyle = computed(() => this.cfg().buttonStyle || 'primary');

  readonly notesText = computed(() => this.cfg().notesText || '');
  readonly submitLabel = computed(() => this.cfg().submitLabel || 'Submit');

  readonly formFields = computed(() => this.cfg().fields ?? []);
  readonly placeholder = computed(() => this.cfg().placeholder || '');

  readonly tableColumns = computed(() => this.cfg().columns ?? []);
  readonly tableRows = computed(() => this.cfg().rows ?? []);

  readonly accept = computed(() => this.cfg().accept || '');
  readonly multiple = computed(() => this.cfg().multiple === true);
  readonly uploadedFileNames = computed(() => this.cfg().uploadedFileNames ?? []);

  // Local interactive state — preview controls are live but don't persist.
  inputValue = '';
  liveSlider: number | null = null;
  liveRangeStart: number | null = null;
  liveRangeEnd: number | null = null;

  // ── Edit handlers (only active when [editable]="true") ──────────────────────
  /** Emit a merged config so the host (dashboard-builder) can persist it. */
  private emitConfig(patch: Partial<WidgetConfig>): void {
    this.configChange.emit({ ...this.cfg(), ...patch });
  }

  onNotesInput(value: string): void {
    this.emitConfig({ notesText: value });
  }

  currentSliderValue(): number {
    return this.liveSlider ?? this.sliderValue();
  }

  currentRangeStart(): number {
    return this.liveRangeStart ?? this.rangeStart();
  }

  currentRangeEnd(): number {
    return this.liveRangeEnd ?? this.rangeEnd();
  }

  onInputValueChange(value: string): void {
    this.inputValue = value;
    this.valueChange.emit({ inputValue: value, widgetValue: value });
  }

  onSliderValueChange(value: number | null): void {
    const next = this.numberOr(value, this.min());
    this.liveSlider = next;
    this.valueChange.emit({ sliderValue: next, widgetValue: next });
  }

  onRangeStartChange(value: number | null): void {
    const next = this.numberOr(value, this.min());
    this.liveRangeStart = next;
    this.valueChange.emit({
      rangeStart: next,
      rangeEnd: this.currentRangeEnd(),
      widgetValue: next,
    });
  }

  onRangeEndChange(value: number | null): void {
    const next = this.numberOr(value, this.max());
    this.liveRangeEnd = next;
    this.valueChange.emit({
      rangeStart: this.currentRangeStart(),
      rangeEnd: next,
      widgetValue: next,
    });
  }

  onFormFieldChange(key: string, value: unknown): void {
    this.valueChange.emit({ [key]: value, inputValue: value, widgetValue: value });
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const selected = input.files ? Array.from(input.files) : [];
    if (!selected.length) return;
    const names = selected.map((file) => file.name);
    const next = this.multiple() ? [...this.uploadedFileNames(), ...names] : names;
    this.emitConfig({ uploadedFileNames: next });
    // Allow re-selecting the same file again.
    input.value = '';
  }

  removeUploadedFile(index: number): void {
    const next = this.uploadedFileNames().filter((_, i) => i !== index);
    this.emitConfig({ uploadedFileNames: next });
  }

  // ── Helpers ───────────────────────────────────────────────────────────────
  private numberOr(value: unknown, fallback: number): number {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
  }

  private clamp(value: number): number {
    return Math.min(this.max(), Math.max(this.min(), value));
  }

  trackByColKey(_: number, col: { key: string }): string {
    return col.key;
  }

  trackByFieldKey(_: number, field: { key: string }): string {
    return field.key;
  }
}
